import { useState, useEffect, useCallback } from 'react';
import { taskService } from '@/lib/taskService';
import type { Task, Column, BoardState, TaskPriority, RecurrencePattern } from '@/components/kanban/types';
import { useAuth } from '@/context/AuthContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { supabase } from '@/lib/supabase';
import { safeLocalStorage } from '@/lib/storage-utils';


export function useBoardState(boardId: string) {
  const { user, profile } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const [state, setState] = useState<BoardState>({
    tasksById: {},
    columns: {},
    columnOrder: [],
    columnMeta: {},
    archived: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load board data from database
  const loadBoardData = useCallback(async (showLoading = false) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);

      const [columnsData, tasksData] = await Promise.all([
        taskService.getColumnsByBoard(boardId),
        taskService.getTasksByBoard(boardId),
      ]);

      // Build column meta and order
      const columnMeta: Record<string, Column> = {};
      const columnOrder: string[] = [];
      const columns: Record<string, string[]> = {};

      // Sort columns by position to ensure correct order
      const sortedColumns = [...columnsData].sort((a, b) => a.position - b.position);
      
      sortedColumns.forEach((col) => {
        columnMeta[col.id] = {
          id: col.id,
          title: col.title,
          bgColor: col.bg_color || '',
        };
        columnOrder.push(col.id);
        columns[col.id] = [];
      });

      // Build tasks and organize by column
      const tasksById: Record<string, Task> = {};
      const archived: string[] = [];

      tasksData.forEach((taskData: any) => {
        let taskUpvotes = taskData.upvotes ?? (Array.isArray(taskData.upvoted_by) ? taskData.upvoted_by.length : 0);
        let taskUpvotedBy = Array.isArray(taskData.upvoted_by) ? taskData.upvoted_by : (Array.isArray(taskData.upvotedBy) ? taskData.upvotedBy : []);

        // Merge with local storage cache fallback
        try {
          const cached = safeLocalStorage.getItem(`task-upvote-data-${taskData.id}`);
          if (cached) {
            const parsed = JSON.parse(cached);
            if (typeof parsed.upvotes === 'number') {
              taskUpvotes = Math.max(taskUpvotes, parsed.upvotes);
            }
            if (Array.isArray(parsed.upvotedBy)) {
              taskUpvotedBy = Array.from(new Set([...taskUpvotedBy, ...parsed.upvotedBy]));
            }
          }
        } catch {
          // ignore
        }

        const task: Task = {
          id: taskData.id,
          title: taskData.title,
          description: taskData.description || '',
          status: taskData.column_id || 'archived',
          priority: taskData.priority as TaskPriority,
          createdAt: taskData.created_at,
          startDate: taskData.start_date,
          dueDate: taskData.due_date,
          archivedAt: taskData.archived_at,
          completed: taskData.completed || false,
          completedAt: taskData.completed_at,
          assignees: taskData.task_assignees?.map((a: any) => ({
            id: a.profiles.id,
            name: `${a.profiles.first_name || ''} ${a.profiles.last_name || ''}`.trim() || a.profiles.email,
            email: a.profiles.email,
            avatar: a.profiles.avatar_url,
          })) || [],
          timeTracking: taskData.task_time_tracking?.[0] ? {
            totalSeconds: taskData.task_time_tracking[0].total_seconds,
            isRunning: taskData.task_time_tracking[0].is_running,
            lastStartTime: taskData.task_time_tracking[0].last_start_time,
          } : undefined,
          messages: taskData.task_messages?.map((m: any) => ({
            id: m.id,
            text: m.text,
            createdAt: m.created_at,
            attachments: m.task_attachments?.map((a: any) => ({
              id: a.id,
              name: a.name,
              size: a.size,
              type: a.type,
              dataUrl: a.url,
              isExternalGif: a.is_external_gif || false,
            })) || [],
          })) || [],
          isRecurring: taskData.is_recurring,
          recurrencePattern: taskData.recurrence_pattern,
          parentTaskId: taskData.parent_task_id,
          upvotes: taskUpvotes,
          upvotedBy: taskUpvotedBy,
        };

        tasksById[task.id] = task;

        if (taskData.status === 'archived') {
          archived.push(task.id);
        } else if (taskData.column_id && columns[taskData.column_id]) {
          columns[taskData.column_id].push(task.id);
        }
      });

      setState({
        tasksById,
        columns,
        columnOrder,
        columnMeta,
        archived,
      });
    } catch (err) {
      console.error('Error loading board data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load board data');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [boardId]);

  useEffect(() => {
    loadBoardData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardId]); // Only reload when boardId changes, not when loadBoardData reference changes

  // Task operations
  const addTask = useCallback(async (data: {
    title: string;
    description?: string;
    priority: TaskPriority;
    startDate?: string;
    dueDate?: string;
    assignees?: { id: string; name: string; email: string; avatar?: string }[];
    targetColumnId?: string;
    isRecurring?: boolean;
    recurrencePattern?: RecurrencePattern;
  }) => {
    if (!user) return;

    try {
      const targetId = data.targetColumnId || state.columnOrder[0];
      const columnData = await taskService.getColumnsByBoard(boardId);
      const column = columnData.find(c => c.id === targetId);

      const task = await taskService.createTask(boardId, {
        title: data.title,
        description: data.description,
        priority: data.priority,
        columnId: column?.id,
        position: 0,
        startDate: data.startDate,
        dueDate: data.dueDate,
        createdBy: user.id,
        isRecurring: data.isRecurring,
        recurrencePattern: data.recurrencePattern,
      });

      // Add assignees
      if (data.assignees && data.assignees.length > 0) {
        await Promise.all(
          data.assignees.map(assignee =>
            taskService.addAssignee(task.id, assignee.id)
          )
        );

        // Send email notifications to all assigned members
        try {
          // Get board information for the email
          const { data: boardData } = await supabase
            .from('task_boards')
            .select('title')
            .eq('id', boardId)
            .single();

          const assignerName = profile 
            ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || profile.email 
            : undefined;

          // Send emails to all assignees
          for (const assignee of data.assignees) {
            try {
              const { error: emailError } = await supabase.functions.invoke('send-task-assignment-email', {
                body: {
                  email: assignee.email,
                  assigneeName: assignee.name,
                  taskTitle: data.title,
                  taskLink: window.location.href,
                  workspaceName: currentWorkspace?.name,
                  assignedBy: assignerName,
                  boardName: boardData?.title || 'Untitled Board',
                  dueDate: data.dueDate,
                  priority: data.priority,
                },
              });

              if (emailError) {
                console.error('Failed to send assignment email to', assignee.email, ':', emailError);
              } else {
                console.log('Assignment email sent to:', assignee.email);
              }
            } catch (emailErr) {
              console.error('Error sending assignment email to', assignee.email, ':', emailErr);
            }
          }
        } catch (err) {
          // Don't fail task creation if email sending fails
          console.error('Error sending assignment emails:', err);
        }
      }

      // Reload board data
      await loadBoardData();
    } catch (err) {
      console.error('Error adding task:', err);
      throw err;
    }
  }, [boardId, user, state.columnOrder, loadBoardData]);

  const updateTask = useCallback(async (taskId: string, updates: Partial<Task>) => {
    try {
      // Prepare database updates
      const dbUpdates: any = {};
      if (updates.title !== undefined) dbUpdates.title = updates.title;
      if (updates.description !== undefined) dbUpdates.description = updates.description;
      if (updates.priority !== undefined) dbUpdates.priority = updates.priority;
      if (updates.startDate !== undefined) dbUpdates.start_date = updates.startDate;
      if (updates.dueDate !== undefined) dbUpdates.due_date = updates.dueDate;
      if (updates.isRecurring !== undefined) dbUpdates.is_recurring = updates.isRecurring;
      if (updates.recurrencePattern !== undefined) dbUpdates.recurrence_pattern = updates.recurrencePattern;
      if (updates.coverImageUrl !== undefined) dbUpdates.cover_image_url = updates.coverImageUrl;
      if (updates.completed !== undefined) dbUpdates.completed = updates.completed;
      if (updates.completedAt !== undefined) dbUpdates.completed_at = updates.completedAt;
      if (updates.upvotes !== undefined) dbUpdates.upvotes = updates.upvotes;
      if (updates.upvotedBy !== undefined) dbUpdates.upvoted_by = updates.upvotedBy;

      // If status changes, update column_id to match the new status
      if (updates.status !== undefined) {
        dbUpdates.status = updates.status;
        dbUpdates.column_id = updates.status;
      }

      await taskService.updateTask(taskId, dbUpdates);

      // Update local state immediately for responsiveness
      setState(prev => {
        const task = prev.tasksById[taskId];
        if (!task) return prev;

        const updatedTask = { ...task, ...updates };

        // If status changed, move task between columns
        if (updates.status && updates.status !== task.status) {
          const oldColumnId = task.status;
          const newColumnId = updates.status;

          // Remove from old column
          const oldColumn = prev.columns[oldColumnId] || [];
          const newOldColumn = oldColumn.filter(id => id !== taskId);

          // Add to new column
          const newColumn = prev.columns[newColumnId] || [];
          const newNewColumn = [...newColumn, taskId];

          return {
            ...prev,
            tasksById: {
              ...prev.tasksById,
              [taskId]: updatedTask,
            },
            columns: {
              ...prev.columns,
              [oldColumnId]: newOldColumn,
              [newColumnId]: newNewColumn,
            },
          };
        }

        // If no status change, just update the task
        return {
          ...prev,
          tasksById: {
            ...prev.tasksById,
            [taskId]: updatedTask,
          },
        };
      });
    } catch (err) {
      console.error('Error updating task:', err);
      throw err;
    }
  }, []);

  const deleteTask = useCallback(async (taskId: string) => {
    try {
      await taskService.deleteTask(taskId);

      setState(prev => {
        const { [taskId]: _omit, ...rest } = prev.tasksById;
        const newCols: BoardState['columns'] = Object.fromEntries(
          Object.entries(prev.columns).map(([k, arr]) => [k, arr.filter((tid) => tid !== taskId)])
        );
        const newArchived = prev.archived.filter((tid) => tid !== taskId);
        return { ...prev, tasksById: rest, columns: newCols, archived: newArchived };
      });
    } catch (err) {
      console.error('Error deleting task:', err);
      throw err;
    }
  }, []);

  const archiveTask = useCallback(async (taskId: string) => {
    try {
      await taskService.updateTask(taskId, {
        status: 'archived',
        archived_at: new Date().toISOString(),
        column_id: null,
      });

      setState(prev => {
        const task = prev.tasksById[taskId];
        if (!task) return prev;

        const updated: Task = { ...task, status: 'archived', archivedAt: new Date().toISOString() };
        const newCols: BoardState['columns'] = Object.fromEntries(
          Object.entries(prev.columns).map(([k, arr]) => [k, arr.filter((tid) => tid !== taskId)])
        );
        return {
          ...prev,
          tasksById: { ...prev.tasksById, [taskId]: updated },
          columns: newCols,
          archived: [taskId, ...prev.archived],
        };
      });
    } catch (err) {
      console.error('Error archiving task:', err);
      throw err;
    }
  }, []);

  const unarchiveTask = useCallback(async (taskId: string) => {
    try {
      // Get the first column ID from state (first in columnOrder = first column by position)
      const firstColumnId = state.columnOrder[0];
      if (!firstColumnId) {
        console.error('No columns found in board');
        return;
      }

      // Get current tasks in the first column to determine position
      const tasksInFirstColumn = state.columns[firstColumnId] || [];
      const newPosition = tasksInFirstColumn.length; // Add to the end of the first column

      await taskService.updateTask(taskId, {
        status: 'active',
        column_id: firstColumnId,
        position: newPosition,
        archived_at: null, // Explicitly set to null to unarchive
      });

      await loadBoardData();
    } catch (err) {
      console.error('Error unarchiving task:', err);
      throw err;
    }
  }, [boardId, loadBoardData, state.columnOrder, state.columns]);

  const moveTask = useCallback(async (taskId: string, toColumnId: string, position: number) => {
    try {
      // Optimistically update local state first for snappy UI
      setState(prev => {
        const task = prev.tasksById[taskId];
        if (!task) return prev;

        const newColumns = { ...prev.columns };

        // Remove from all columns
        Object.keys(newColumns).forEach(colId => {
          newColumns[colId] = newColumns[colId].filter(id => id !== taskId);
        });

        // Add to new column at the specified position
        if (!newColumns[toColumnId]) newColumns[toColumnId] = [];
        newColumns[toColumnId].splice(position, 0, taskId);

        return {
          ...prev,
          columns: newColumns,
          tasksById: {
            ...prev.tasksById,
            [taskId]: { ...task, status: toColumnId },
          },
        };
      });

      // Then update the database
      await taskService.moveTask(taskId, toColumnId, position);
    } catch (err) {
      console.error('Error moving task:', err);
      // Reload data on error to restore correct state
      await loadBoardData();
      throw err;
    }
  }, [loadBoardData]);

  // Column operations
  const addColumn = useCallback(async (title: string, bgColor?: string) => {
    try {
      const position = state.columnOrder.length;
      const created = await taskService.createColumn(boardId, title, position, bgColor);
      if (created) {
        setState(prev => ({
          ...prev,
          columnOrder: [...prev.columnOrder, created.id],
          columnMeta: {
            ...prev.columnMeta,
            [created.id]: {
              id: created.id,
              title: created.title,
              bgColor: created.bg_color || bgColor || '',
            },
          },
          columns: {
            ...prev.columns,
            [created.id]: [],
          },
        }));
      }
      await loadBoardData(false);
      return created;
    } catch (err) {
      console.error('Error adding column:', err);
      throw err;
    }
  }, [boardId, state.columnOrder.length, loadBoardData]);

  const updateColumnTitle = useCallback(async (columnId: string, title: string) => {
    try {
      await taskService.updateColumn(columnId, { title });

      setState(prev => ({
        ...prev,
        columnMeta: {
          ...prev.columnMeta,
          [columnId]: { ...prev.columnMeta[columnId], title },
        },
      }));
    } catch (err) {
      console.error('Error updating column:', err);
      throw err;
    }
  }, []);

  const updateColumnBgColor = useCallback(async (columnId: string, bgColor: string) => {
    try {
      await taskService.updateColumn(columnId, { bg_color: bgColor });

      setState(prev => ({
        ...prev,
        columnMeta: {
          ...prev.columnMeta,
          [columnId]: { ...prev.columnMeta[columnId], bgColor },
        },
      }));
    } catch (err) {
      console.error('Error updating column color:', err);
      throw err;
    }
  }, []);

  const deleteColumn = useCallback(async (columnId: string) => {
    try {
      setState(prev => {
        const nextOrder = prev.columnOrder.filter(id => id !== columnId);
        const nextMeta = { ...prev.columnMeta };
        delete nextMeta[columnId];
        const nextColumns = { ...prev.columns };
        delete nextColumns[columnId];
        return {
          ...prev,
          columnOrder: nextOrder,
          columnMeta: nextMeta,
          columns: nextColumns,
        };
      });

      await taskService.deleteColumn(columnId);
      await loadBoardData(false);
    } catch (err) {
      console.error('Error deleting column:', err);
      await loadBoardData(false);
      throw err;
    }
  }, [loadBoardData]);

  const reorderColumns = useCallback(async (newColumnOrder: string[]) => {
    try {
      // Optimistically update local state first
      setState(prev => ({
        ...prev,
        columnOrder: newColumnOrder,
      }));

      // Then update the database
      await taskService.reorderColumns(boardId, newColumnOrder);
    } catch (err) {
      console.error('Error reordering columns:', err);
      // Reload data on error to restore correct state
      await loadBoardData();
      throw err;
    }
  }, [boardId, loadBoardData]);

  const toggleUpvote = useCallback(async (taskId: string) => {
    const task = state.tasksById[taskId];
    if (!task) return;

    const currentUserId = user?.id || profile?.id || 'current_user';
    const currentUpvotedBy = Array.isArray(task.upvotedBy) ? [...task.upvotedBy] : [];
    const hasUpvoted = currentUpvotedBy.includes(currentUserId);

    const newUpvotedBy = hasUpvoted
      ? currentUpvotedBy.filter(id => id !== currentUserId)
      : [...currentUpvotedBy, currentUserId];
    const newUpvotes = Math.max(0, (task.upvotes ?? currentUpvotedBy.length) + (hasUpvoted ? -1 : 1));

    // Persist to local storage cache immediately
    try {
      safeLocalStorage.setItem(`task-upvote-data-${taskId}`, JSON.stringify({
        upvotes: newUpvotes,
        upvotedBy: newUpvotedBy,
      }));
    } catch {
      // ignore
    }

    // Optimistically update state
    setState(prev => {
      const current = prev.tasksById[taskId];
      if (!current) return prev;
      return {
        ...prev,
        tasksById: {
          ...prev.tasksById,
          [taskId]: {
            ...current,
            upvotes: newUpvotes,
            upvotedBy: newUpvotedBy,
          },
        },
      };
    });

    try {
      await taskService.updateTask(taskId, {
        upvotes: newUpvotes,
        upvoted_by: newUpvotedBy,
      });
    } catch (err) {
      console.error('Error updating task upvote:', err);
    }
  }, [state.tasksById, user?.id, profile?.id]);

  return {
    state,
    loading,
    error,
    addTask,
    updateTask,
    deleteTask,
    archiveTask,
    unarchiveTask,
    moveTask,
    addColumn,
    updateColumnTitle,
    updateColumnBgColor,
    deleteColumn,
    reorderColumns,
    toggleUpvote,
    reload: loadBoardData,
  };
}
